import type { ApiEndpoint } from "@/shared/api-endpoint";
import { kMusicalKeys } from "@/shared/musical-key.api";
import { isObject, isStringArray } from "@/shared/utils/typeGuards";

export const kStackTemplatesRoute = "/stack-templates";
export const kNewStackRoute = "/new-stack";

/** A genre template the user can build a stack from, e.g. `lo-fi`, `house`. */
export interface StackTemplate {
    slug: string;
    label: string;
    description: string;
    /** `true` for templates gated behind a paid entitlement. */
    isPremium: boolean;
}

const isStackTemplate = (value: unknown): value is StackTemplate =>
    isObject<StackTemplate>(value) &&
    typeof value.slug === "string" &&
    typeof value.label === "string" &&
    typeof value.description === "string" &&
    typeof value.isPremium === "boolean";

/**
 * One layer of a stack, flattened to what playback needs.
 *
 * A layer keeps its source loop's tempo (`sourceBpm`) and the player stretches
 * it to the stack's tempo — so the two differing is the normal case, not a bug.
 */
export interface StackLayerSpec {
    uuid: string;
    /** `drums`, `bass`, `chords`, … — the layer's slot in the arrangement. */
    layerType: string;
    /** Catalog asset the layer plays. Absent for a user-audio layer. */
    assetUuid: string | undefined;
    /**
     * Set instead of `assetUuid` when the layer is the user's own audio, which
     * a seeded stack puts in its first lane.
     */
    userAudioFileHash: string | undefined;
    /**
     * Where to fetch a user-audio layer's file. Their own upload, so it comes
     * down ready to play rather than needing the descramble a catalog preview
     * does.
     */
    userAudioUrl: string | undefined;
    /** Filename of the source loop, for display. */
    assetName: string;
    /** Tempo of the source loop, which the player stretches to the stack tempo. */
    sourceBpm: number;
    numBars: number;
    /** Semitones to shift this layer, before any stack-wide offset. */
    psOffset: number;
    /** The layer's own level as Splice has it, linear with 1 as unity. */
    volume: number;
    /**
     * Whether a stack-wide pitch shift reaches this layer. Drum and percussion
     * layers are unpitched, so transposing the stack must leave them alone.
     */
    doesFollowStackPitch: boolean;
    isMutedByDefault: boolean;
}

const isStackLayerSpec = (value: unknown): value is StackLayerSpec =>
    isObject<StackLayerSpec>(value) &&
    typeof value.uuid === "string" &&
    typeof value.layerType === "string" &&
    (value.assetUuid === undefined || typeof value.assetUuid === "string") &&
    (value.userAudioFileHash === undefined || typeof value.userAudioFileHash === "string") &&
    (value.userAudioUrl === undefined || typeof value.userAudioUrl === "string") &&
    typeof value.assetName === "string" &&
    typeof value.sourceBpm === "number" &&
    typeof value.numBars === "number" &&
    typeof value.psOffset === "number" &&
    typeof value.volume === "number" &&
    typeof value.doesFollowStackPitch === "boolean" &&
    typeof value.isMutedByDefault === "boolean";

export interface StackSpec {
    uuid: string;
    template: string;
    stackBpm: number;
    /** Semitones the whole stack is transposed by. */
    stackPsOffset: number;
    /** Root note of the estimated key, lowercase with sharps (`"d#"`), or "". */
    keyRoot: string;
    /** `"major"`, `"minor"`, or "" when the estimate has no quality. */
    keyChordType: string;
    layers: StackLayerSpec[];
}

const isStackSpec = (value: unknown): value is StackSpec =>
    isObject<StackSpec>(value) &&
    typeof value.uuid === "string" &&
    typeof value.template === "string" &&
    typeof value.stackBpm === "number" &&
    typeof value.stackPsOffset === "number" &&
    typeof value.keyRoot === "string" &&
    typeof value.keyChordType === "string" &&
    Array.isArray(value.layers) &&
    value.layers.every(isStackLayerSpec);

// ------------------------------- Templates -----------------------------------

// The template list takes no arguments, but every Labs capability is a POST
// with a JSON body, so the body is an empty object rather than a special case.
export type StackTemplatesRequestBody = Record<string, never>;

export const isStackTemplatesRequestBody = (value: unknown): value is StackTemplatesRequestBody =>
    isObject(value);

export interface StackTemplatesSuccessResponse {
    ok: true;
    templates: StackTemplate[];
}

export const isStackTemplatesSuccessResponse = (
    value: unknown,
): value is StackTemplatesSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    Array.isArray(value.templates) &&
    value.templates.every(isStackTemplate);

export const kStackTemplatesEndpoint: ApiEndpoint<
    StackTemplatesRequestBody,
    StackTemplatesSuccessResponse
> = {
    route: kStackTemplatesRoute,
    label: "Stack Templates",
    source: "Splice",
    category: "Stacks",
    description:
        "Lists the genre templates a stack can be built from, with the labels and descriptions the Splice create UX shows.",
    isRequestBody: isStackTemplatesRequestBody,
    isSuccessResponse: isStackTemplatesSuccessResponse,
};

// ------------------------------- New stack -----------------------------------

export interface NewStackRequestBody {
    template: string;
    bpm?: number;
    /**
     * Catalog asset to build the stack around — the uuid that arrives with a
     * loop dragged out of the Splice browser. The seeded loop comes back as one
     * of the layers and the rest are chosen to sit with it.
     */
    seedAssetUuid?: string;
    /**
     * The user's own audio to build around, as the hash Splice gives it once
     * the upload has been processed.
     */
    seedUserAudioFileHash?: string;
    /**
     * The stack being replaced, with the edits made to it, so layers kept from
     * it carry the volume and mute they are being heard at.
     */
    payload?: StackPayload;
    edits?: StackEdits;
    /**
     * Layers of that stack to keep. A locked layer survives a shuffle or a
     * change of genre: Splice is asked to build around it rather than to start
     * over. Named by uuid because the layer inputs themselves are the server's
     * to read out of the payload.
     */
    lockedLayerUuids?: string[];
}

export const isNewStackRequestBody = (value: unknown): value is NewStackRequestBody =>
    isObject<NewStackRequestBody>(value) &&
    typeof value.template === "string" &&
    (value.bpm === undefined || typeof value.bpm === "number") &&
    (value.seedAssetUuid === undefined || typeof value.seedAssetUuid === "string") &&
    (value.seedUserAudioFileHash === undefined ||
        typeof value.seedUserAudioFileHash === "string") &&
    (value.payload === undefined || isStackPayload(value.payload)) &&
    (value.edits === undefined || isStackEdits(value.edits)) &&
    (value.lockedLayerUuids === undefined ||
        (Array.isArray(value.lockedLayerUuids) &&
            value.lockedLayerUuids.every((uuid) => typeof uuid === "string")));

export interface NewStackSuccessResponse extends StackAndPayload {
    ok: true;
}

export const isNewStackSuccessResponse = (value: unknown): value is NewStackSuccessResponse =>
    isObject(value) && value.ok === true && isStackAndPayload(value);

export const kNewStackEndpoint: ApiEndpoint<NewStackRequestBody, NewStackSuccessResponse> = {
    route: kNewStackRoute,
    label: "New Stack",
    source: "Splice",
    category: "Stacks",
    description:
        "Builds a stack from a genre template, optionally seeded with a catalog loop, returning the layers with the tempo and pitch each one needs to line up.",
    isRequestBody: isNewStackRequestBody,
    isSuccessResponse: isNewStackSuccessResponse,
};

/**
 * Beats per bar the stack API assumes. Stacks are 4/4 throughout — layer length
 * arrives as a bar count with no time signature alongside it.
 */
export const kStackBeatsPerBar = 4;

/**
 * The tempos a stack can be asked to build at.
 *
 * Shared so the field people type into and the validator that accepts it cannot
 * drift apart — the server states it as TypeBox `minimum`/`maximum`, which are
 * different names for the same two numbers.
 */
export const kStackBpmRange = { min: 40, max: 220 } as const;

/**
 * Bars the whole stack spans, which is its longest layer's loop. Shorter layers
 * are powers-of-two fractions of it, so they repeat a whole number of times
 * inside it.
 */
export const stackLengthInBars = (stack: Readonly<StackSpec>): number =>
    stack.layers.reduce((longest, layer) => Math.max(longest, layer.numBars), 1);

/** Musical length of a layer, in beats at its own source tempo. */
export const stackLayerLengthInBeats = (layer: Readonly<StackLayerSpec>): number =>
    layer.numBars * kStackBeatsPerBar;

/**
 * How long a layer's loop runs at its source tempo. The player derives the loop
 * length from this rather than from the decoded file, whose length an MP3
 * encoder pads either side of the music.
 */
export const stackLayerDuration_s = (layer: Readonly<StackLayerSpec>): number =>
    (stackLayerLengthInBeats(layer) / layer.sourceBpm) * 60;

/** Semitones a layer plays at, once the stack-wide transpose is folded in. */
export const stackLayerPitchShift = (
    layer: Readonly<StackLayerSpec>,
    stackPsOffset: number,
): number => layer.psOffset + (layer.doesFollowStackPitch ? stackPsOffset : 0);

// ------------------------------ Round-tripping --------------------------------

/**
 * The stack as Splice's own API wants it back.
 *
 * Every operation after the first — swapping a layer, adding one, saving —
 * takes the whole current stack as its input, and it has to be the *whole*
 * stack: the per-layer embeddings the compatibility search runs on, the
 * colours and labels the platform's own UI assigned, each layer's lock state.
 * A Labs app has no use for any of that, and dropping it would quietly produce
 * saved stacks that reopen degraded.
 *
 * So it travels as an opaque payload. The server builds it, the client holds it
 * without reading it and hands it back, and the server is the only place that
 * knows the shape. `StackSpec` stays the view playback needs.
 */
export type StackPayload = Record<string, unknown>;

export const isStackPayload = (value: unknown): value is StackPayload => isObject(value);

/** A stack and the payload that can be sent back to act on it. */
export interface StackAndPayload {
    stack: StackSpec;
    payload: StackPayload;
}

const isStackAndPayload = (value: unknown): value is StackAndPayload =>
    isObject(value) && isStackSpec(value.stack) && isStackPayload(value.payload);

// --------------------------------- Edits --------------------------------------

/**
 * Unity for `StackLayerProperties.volume`, which is a **linear multiplier**
 * rather than decibels: a stack comes back from Splice with every layer at 1.
 *
 * Worth being explicit, because the platform's own converter defaults a
 * missing volume to `0` — which reads like a dB unity and is really just a bad
 * default. Writing 0 here is writing silence, and a stack saved that way plays
 * silently on Splice until someone nudges each layer.
 */
export const kStackLayerVolumeUnity = 1;

export const kStackLayerVolumeRange = { min: 0, max: 1 } as const;

/** Semitones the whole stack can be transposed by, matching Splice's own range. */
export const kStackPitchRange = { min: -12, max: 12 } as const;

/** The twelve roots, in semitone order, for reading a transpose back as a key. */
const kPitchClasses: readonly string[] = kMusicalKeys.filter((key) => key !== "");

/**
 * The key a stack lands in once it is transposed. Returns "" when the root is
 * not one we recognise, which is also what an unknown estimate looks like.
 */
export const transposeKeyRoot = (root: string, semitones: number): string => {
    const index = kPitchClasses.indexOf(root.toLowerCase());
    if (index < 0) {
        return "";
    }
    const shifted = (((index + semitones) % 12) + 12) % 12;
    return kPitchClasses[shifted];
};

/** What the user changed about a stack after Splice handed it over. */
export interface StackLayerEdit {
    uuid: string;
    /** Linear, where 1 is unity — see `kStackLayerVolumeUnity`. */
    volume: number;
    isMuted: boolean;
    /**
     * Locked layers are the ones the user wants kept. Splice stores the flag
     * on the layer, and here it also takes the layer out of reach of a swap.
     */
    isLocked: boolean;
}

const isStackLayerEdit = (value: unknown): value is StackLayerEdit =>
    isObject<StackLayerEdit>(value) &&
    typeof value.uuid === "string" &&
    typeof value.volume === "number" &&
    typeof value.isMuted === "boolean" &&
    typeof value.isLocked === "boolean";

export interface StackEdits {
    /** Semitones the whole stack is transposed by. */
    stackPsOffset: number;
    layers: StackLayerEdit[];
    /** Layers the user took out, by id. */
    removedLayerUuids: string[];
}

export const isStackEdits = (value: unknown): value is StackEdits =>
    isObject<StackEdits>(value) &&
    typeof value.stackPsOffset === "number" &&
    Array.isArray(value.layers) &&
    value.layers.every(isStackLayerEdit) &&
    Array.isArray(value.removedLayerUuids) &&
    isStringArray(value.removedLayerUuids);

// ------------------------------- Layer swap -----------------------------------

export const kSwapStackLayerRoute = "/swap-stack-layer";

export interface SwapStackLayerRequestBody {
    payload: StackPayload;
    edits: StackEdits;
    /**
     * Which layer to replace, by id rather than position — removing a layer
     * shifts every position after it, and the server applies the edits before
     * it works out where the layer now sits.
     */
    layerUuid: string;
    /**
     * How far down the compatibility ranking to reach. Raising it each press is
     * what makes the swap button cycle rather than return the same loop.
     */
    offset: number;
}

export const isSwapStackLayerRequestBody = (value: unknown): value is SwapStackLayerRequestBody =>
    isObject(value) &&
    isStackPayload(value.payload) &&
    isStackEdits(value.edits) &&
    typeof value.layerUuid === "string" &&
    typeof value.offset === "number";

export interface SwapStackLayerSuccessResponse extends StackAndPayload {
    ok: true;
}

export const isSwapStackLayerSuccessResponse = (
    value: unknown,
): value is SwapStackLayerSuccessResponse =>
    isObject(value) && value.ok === true && isStackAndPayload(value);

export const kSwapStackLayerEndpoint: ApiEndpoint<
    SwapStackLayerRequestBody,
    SwapStackLayerSuccessResponse
> = {
    route: kSwapStackLayerRoute,
    label: "Swap Stack Layer",
    source: "Splice",
    category: "Stacks",
    description:
        "Replaces one layer of a stack with the next best-fitting loop, keeping the rest of the arrangement in place.",
    isRequestBody: isSwapStackLayerRequestBody,
    isSuccessResponse: isSwapStackLayerSuccessResponse,
};

// -------------------------------- Add layer -----------------------------------

export const kAddStackLayerRoute = "/add-stack-layer";

export interface AddStackLayerRequestBody {
    payload: StackPayload;
    edits: StackEdits;
    /** `bass`, `chords`, `vocals`, … — a slug from the layer-type list. */
    layerType: string;
}

export const isAddStackLayerRequestBody = (value: unknown): value is AddStackLayerRequestBody =>
    isObject(value) &&
    isStackPayload(value.payload) &&
    isStackEdits(value.edits) &&
    typeof value.layerType === "string";

export interface AddStackLayerSuccessResponse extends StackAndPayload {
    ok: true;
}

export const isAddStackLayerSuccessResponse = (
    value: unknown,
): value is AddStackLayerSuccessResponse =>
    isObject(value) && value.ok === true && isStackAndPayload(value);

export const kAddStackLayerEndpoint: ApiEndpoint<
    AddStackLayerRequestBody,
    AddStackLayerSuccessResponse
> = {
    route: kAddStackLayerRoute,
    label: "Add Stack Layer",
    source: "Splice",
    category: "Stacks",
    description:
        "Adds a layer of the requested type to a stack, chosen to fit what is already there.",
    isRequestBody: isAddStackLayerRequestBody,
    isSuccessResponse: isAddStackLayerSuccessResponse,
};

// --------------------------------- Add a loop ---------------------------------

export const kAddStackLoopRoute = "/add-stack-loop";

export interface AddStackLoopRequestBody {
    payload: StackPayload;
    edits: StackEdits;
    /** A catalog uuid, as `/resolve-seed` returns it. */
    assetUuid: string;
}

export const isAddStackLoopRequestBody = (value: unknown): value is AddStackLoopRequestBody =>
    isObject(value) &&
    isStackPayload(value.payload) &&
    isStackEdits(value.edits) &&
    typeof value.assetUuid === "string";

export interface AddStackLoopSuccessResponse extends StackAndPayload {
    ok: true;
}

export const isAddStackLoopSuccessResponse = (
    value: unknown,
): value is AddStackLoopSuccessResponse =>
    isObject(value) && value.ok === true && isStackAndPayload(value);

/**
 * A loop the user picked, rather than one the generator chose.
 *
 * Kept apart from `/add-stack-layer` because the two are different capabilities
 * even though they end in the same place: one asks Splice what would fit, the
 * other is told. Reporting them under one name would hide which of the two
 * people actually reach for.
 */
export const kAddStackLoopEndpoint: ApiEndpoint<
    AddStackLoopRequestBody,
    AddStackLoopSuccessResponse
> = {
    route: kAddStackLoopRoute,
    label: "Add Stack Loop",
    source: "Splice",
    category: "Stacks",
    description: "Adds a chosen catalog loop to a stack as a new layer.",
    isRequestBody: isAddStackLoopRequestBody,
    isSuccessResponse: isAddStackLoopSuccessResponse,
};

// --------------------------------- Layer types --------------------------------

export const kStackLayerTypesRoute = "/stack-layer-types";

export interface StackLayerType {
    slug: string;
    label: string;
    /** Hex colour the platform's own UI gives this layer type. */
    color: string;
}

const isStackLayerType = (value: unknown): value is StackLayerType =>
    isObject<StackLayerType>(value) &&
    typeof value.slug === "string" &&
    typeof value.label === "string" &&
    typeof value.color === "string";

export type StackLayerTypesRequestBody = Record<string, never>;

export const isStackLayerTypesRequestBody = (value: unknown): value is StackLayerTypesRequestBody =>
    isObject(value);

export interface StackLayerTypesSuccessResponse {
    ok: true;
    layerTypes: StackLayerType[];
}

export const isStackLayerTypesSuccessResponse = (
    value: unknown,
): value is StackLayerTypesSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    Array.isArray(value.layerTypes) &&
    value.layerTypes.every(isStackLayerType);

export const kStackLayerTypesEndpoint: ApiEndpoint<
    StackLayerTypesRequestBody,
    StackLayerTypesSuccessResponse
> = {
    route: kStackLayerTypesRoute,
    label: "Stack Layer Types",
    source: "Splice",
    category: "Stacks",
    description: "Lists the layer types a stack can hold, with the labels and colours Splice uses.",
    isRequestBody: isStackLayerTypesRequestBody,
    isSuccessResponse: isStackLayerTypesSuccessResponse,
};

// ---------------------------------- Saving ------------------------------------

export const kSaveStackRoute = "/save-stack";

export interface SaveStackRequestBody {
    payload: StackPayload;
    edits: StackEdits;
    name: string;
    /**
     * Set when re-saving a stack already in the library, together with the
     * version it was last read at — the API rejects a write against a stale
     * one rather than silently overwriting a change made elsewhere.
     */
    uuid?: string;
    version?: number;
}

export const isSaveStackRequestBody = (value: unknown): value is SaveStackRequestBody =>
    isObject(value) &&
    isStackPayload(value.payload) &&
    isStackEdits(value.edits) &&
    typeof value.name === "string" &&
    (value.uuid === undefined || typeof value.uuid === "string") &&
    (value.version === undefined || typeof value.version === "number");

/** A stack as it exists in the user's library. */
export interface SavedStack {
    uuid: string;
    name: string;
    version: number;
}

const isSavedStack = (value: unknown): value is SavedStack =>
    isObject<SavedStack>(value) &&
    typeof value.uuid === "string" &&
    typeof value.name === "string" &&
    typeof value.version === "number";

export interface SaveStackSuccessResponse {
    ok: true;
    saved: SavedStack;
}

export const isSaveStackSuccessResponse = (value: unknown): value is SaveStackSuccessResponse =>
    isObject(value) && value.ok === true && isSavedStack(value.saved);

export const kSaveStackEndpoint: ApiEndpoint<SaveStackRequestBody, SaveStackSuccessResponse> = {
    route: kSaveStackRoute,
    label: "Save Stack",
    source: "Splice",
    category: "Stacks",
    description:
        "Saves a stack to the user's Splice library, where it appears alongside the ones made in Splice's own create surfaces.",
    isRequestBody: isSaveStackRequestBody,
    isSuccessResponse: isSaveStackSuccessResponse,
};

// ------------------------------ Seed parsing ----------------------------------

const kCatalogUuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * The other identifier Splice samples carry: 64 hex characters.
 *
 * This is what a sample's own page URL is built from
 * (`splice.com/sounds/sample/<id>`), and it is the asset's `legacy_uuid`. The
 * Stacks API will not take one as a seed, so it has to be looked up and traded
 * for the asset's `catalog_uuid` first — see `resolveSeedAssetUuid`.
 */
const kLegacyAssetIdPattern = /\b[0-9a-f]{64}\b/i;

export const isLegacyAssetId = (assetId: string): boolean => /^[0-9a-f]{64}$/i.test(assetId);

export type SeedInputResult =
    | { kind: "empty" }
    | { kind: "assetId"; assetId: string }
    | { kind: "unrecognized" };

/**
 * Reads whatever the user pasted into the seed field.
 *
 * Both of Splice's sample identifiers are accepted, in either a bare form or
 * anywhere inside a link — which covers the `sounds/sample/<id>` URL a sample's
 * page has, the `sounds/samples/<id>/<slug>` permalink, and links carrying
 * query parameters.
 */
export const parseSeedInput = (input: string): SeedInputResult => {
    const trimmed = input.trim();
    if (trimmed === "") {
        return { kind: "empty" };
    }

    const catalogUuid = kCatalogUuidPattern.exec(trimmed);
    if (catalogUuid !== null) {
        return { kind: "assetId", assetId: catalogUuid[0].toLowerCase() };
    }

    const legacyId = kLegacyAssetIdPattern.exec(trimmed);
    if (legacyId !== null) {
        return { kind: "assetId", assetId: legacyId[0].toLowerCase() };
    }

    return { kind: "unrecognized" };
};

// ----------------------------- Seed resolution --------------------------------

export const kResolveSeedRoute = "/resolve-seed";

export interface ResolveSeedRequestBody {
    /** Either identifier form, as `parseSeedInput` returned it. */
    assetId: string;
}

export const isResolveSeedRequestBody = (value: unknown): value is ResolveSeedRequestBody =>
    isObject(value) && typeof value.assetId === "string";

/** A sample looked up before it is used as a seed. */
export interface ResolvedSeed {
    /** The catalog id, which is the only form the Stacks API takes. */
    assetUuid: string;
    name: string;
    bpm: number;
    key: string;
    chordType: string;
    /**
     * Whether Splice has indexed this sample for Stacks. An unindexed one is
     * rejected outright, so it is worth knowing before a stack is asked for.
     */
    isEligible: boolean;
}

const isResolvedSeed = (value: unknown): value is ResolvedSeed =>
    isObject<ResolvedSeed>(value) &&
    typeof value.assetUuid === "string" &&
    typeof value.name === "string" &&
    typeof value.bpm === "number" &&
    typeof value.key === "string" &&
    typeof value.chordType === "string" &&
    typeof value.isEligible === "boolean";

export interface ResolveSeedSuccessResponse {
    ok: true;
    seed: ResolvedSeed;
}

export const isResolveSeedSuccessResponse = (value: unknown): value is ResolveSeedSuccessResponse =>
    isObject(value) && value.ok === true && isResolvedSeed(value.seed);

export const kResolveSeedEndpoint: ApiEndpoint<ResolveSeedRequestBody, ResolveSeedSuccessResponse> =
    {
        route: kResolveSeedRoute,
        label: "Resolve Stack Seed",
        source: "Splice",
        category: "Stacks",
        description:
            "Looks up a sample by either of its identifiers, returning the catalog id a stack can be seeded with and whether Splice has indexed it for Stacks.",
        isRequestBody: isResolveSeedRequestBody,
        isSuccessResponse: isResolveSeedSuccessResponse,
    };

/** The seed named the way the app shows it: what it is, how fast, in what key. */
export const describeSeed = (seed: Readonly<ResolvedSeed>): string => {
    const fileName = seed.name.split("/").at(-1) ?? seed.name;
    const parts: string[] = [fileName];
    if (seed.bpm > 0) {
        parts.push(`${String(Math.round(seed.bpm))} bpm`);
    }
    if (seed.key !== "") {
        parts.push(
            seed.chordType === ""
                ? seed.key.toUpperCase()
                : `${seed.key.toUpperCase()} ${seed.chordType}`,
        );
    }
    return parts.join(" · ");
};

/** Why an unindexed sample cannot seed a stack, in words that say what to do. */
export const kSeedNotEligibleMessage =
    "Splice has not indexed that sample for Stacks, so it cannot be used as a seed. Try another loop.";

// --------------------------- User audio uploads -------------------------------

export const kUserAudioUploadUrlRoute = "/user-audio-upload-url";
export const kUserAudioUploadJobRoute = "/user-audio-upload-job";

export interface UserAudioUploadUrlRequestBody {
    fileName: string;
    bpm?: number;
}

export const isUserAudioUploadUrlRequestBody = (
    value: unknown,
): value is UserAudioUploadUrlRequestBody =>
    isObject(value) &&
    typeof value.fileName === "string" &&
    (value.bpm === undefined || typeof value.bpm === "number");

export interface UserAudioUpload {
    jobUuid: string;
    uploadUrl: string;
}

export interface UserAudioUploadUrlSuccessResponse {
    ok: true;
    upload: UserAudioUpload;
}

export const isUserAudioUploadUrlSuccessResponse = (
    value: unknown,
): value is UserAudioUploadUrlSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    isObject(value.upload) &&
    typeof value.upload.jobUuid === "string" &&
    typeof value.upload.uploadUrl === "string";

export const kUserAudioUploadUrlEndpoint: ApiEndpoint<
    UserAudioUploadUrlRequestBody,
    UserAudioUploadUrlSuccessResponse
> = {
    route: kUserAudioUploadUrlRoute,
    label: "User Audio Upload URL",
    source: "Splice",
    category: "Stacks",
    description:
        "Starts an upload of the user's own audio into their Splice storage, returning the signed URL to send the file to.",
    isRequestBody: isUserAudioUploadUrlRequestBody,
    isSuccessResponse: isUserAudioUploadUrlSuccessResponse,
};

export interface UserAudioUploadJobRequestBody {
    jobUuid: string;
}

export const isUserAudioUploadJobRequestBody = (
    value: unknown,
): value is UserAudioUploadJobRequestBody => isObject(value) && typeof value.jobUuid === "string";

/**
 * Where an upload has got to. Splice processes the file after it lands, so the
 * hash a stack can be seeded with only exists once the job completes.
 */
export interface UserAudioUploadJob {
    status: string;
    isComplete: boolean;
    /** Set once complete; this is what seeds the stack. */
    fileHash: string;
}

export interface UserAudioUploadJobSuccessResponse {
    ok: true;
    job: UserAudioUploadJob;
}

export const isUserAudioUploadJobSuccessResponse = (
    value: unknown,
): value is UserAudioUploadJobSuccessResponse =>
    isObject(value) &&
    value.ok === true &&
    isObject(value.job) &&
    typeof value.job.status === "string" &&
    typeof value.job.isComplete === "boolean" &&
    typeof value.job.fileHash === "string";

export const kUserAudioUploadJobEndpoint: ApiEndpoint<
    UserAudioUploadJobRequestBody,
    UserAudioUploadJobSuccessResponse
> = {
    route: kUserAudioUploadJobRoute,
    label: "User Audio Upload Job",
    source: "Splice",
    category: "Stacks",
    description: "Reports how far an upload of the user's own audio has got.",
    isRequestBody: isUserAudioUploadJobRequestBody,
    isSuccessResponse: isUserAudioUploadJobSuccessResponse,
};

/** What an upload status means, in words the user can act on. */
export const describeUploadFailure = (status: string): string => {
    switch (status) {
        case "storage_limit_exceeded":
            return "Your Splice audio storage is full.";
        case "file_too_large":
            return "That file is too large for Splice audio storage.";
        case "invalid_mime_type":
            return "Splice did not accept that file's format.";
        case "invalid_bpm":
            return "Splice could not work with that tempo.";
        case "validation_failed":
            return "Splice could not read that file.";
        case "quarantined":
            return "Splice quarantined that file.";
        default:
            return `The upload failed (${status}).`;
    }
};

export const kUserAudioTerminalFailures: readonly string[] = [
    "storage_limit_exceeded",
    "file_too_large",
    "invalid_mime_type",
    "invalid_bpm",
    "validation_failed",
    "quarantined",
    "internal_error",
];
